import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useGetLiveScoringStatusQuery } from '@/api/live-scoring/getLiveScoringStatus';
import { useGetQuestionLiveStatusQuery } from '@/api/live-scoring/getQuestionLiveStatus';
import { useSetManualScoringMutation } from '@/api/live-scoring/manualScoring';
import { useManualScoreSquareMutation } from '@/api/squares/manualScoreSquare';
import { useGetFixtureScoresQuery } from '@/api/squares/getFixtureScores';
import { Loader2, Play, CheckCircle2, RefreshCw, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { InfoIconDialog } from '@/components/ui/info-icon-dialog';

interface LiveScoringSectionProps {
  questionId: string | null;
  disabled?: boolean;
  /** Styling logos from contest (row=home, col=away) - used when fixture has no logoUrl */
  rowTeamIconUrl?: string | null;
  colTeamIconUrl?: string | null;
  /** Sport/league (e.g. NBA, NFL, NCAAMB) - determines period display (NCAAB shows HT/FT) */
  sport?: string | null;
}

function fallbackAbbr(name?: string | null): string {
  if (!name) return '---';
  return name
    .replace(/[^a-zA-Z]/g, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 3)
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .padEnd(3, '-')
    .slice(0, 3);
}

export function LiveScoringSection({
  questionId,
  disabled = false,
  rowTeamIconUrl,
  colTeamIconUrl,
  sport,
}: LiveScoringSectionProps) {
  const { isLoading: statusLoading } = useGetLiveScoringStatusQuery();
  const { data: questionStatus, isLoading: questionStatusLoading } =
    useGetQuestionLiveStatusQuery(questionId);
  const {
    data: fixtureScores,
    isLoading: fixtureScoresLoading,
    isFetching: fixtureScoresFetching,
    refetch: refetchFixtureScores,
  } = useGetFixtureScoresQuery(questionId);
  const setManualScoringMutation = useSetManualScoringMutation(questionId || '');
  const manualScoreMutation = useManualScoreSquareMutation();

  const [manualScoringEnabled, setManualScoringEnabled] = useState(false);

  // Sync manual scoring state from query
  useEffect(() => {
    if (questionStatus) {
      setManualScoringEnabled(questionStatus.isManualScoring);
    }
  }, [questionStatus]);

  const handleToggleManualScoring = async (enabled: boolean) => {
    if (!questionId) {
      toast.error('Question ID is required');
      return;
    }

    try {
      await setManualScoringMutation.mutateAsync(enabled);
      setManualScoringEnabled(enabled);
      toast.success(
        enabled
          ? 'Manual scoring enabled. Live scoring will be skipped for this question.'
          : 'Manual scoring disabled. Live scoring will grade this question automatically.'
      );
    } catch (error) {
      toast.error('Failed to update manual scoring setting');
      console.error('Failed to toggle manual scoring:', error);
    }
  };

  const handleGradeFromFeed = async () => {
    if (!questionId) {
      toast.error('Question ID is required');
      return;
    }

    try {
      await manualScoreMutation.mutateAsync(questionId);
      // Success toast is handled by the mutation
    } catch (error) {
      console.error('Failed to grade from feed:', error);
      // Error toast is handled by the mutation
    }
  };

  if (!questionId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Live Auto Scoring</h3>
          <InfoIconDialog title="Live Auto Scoring" ariaLabel="Live auto scoring information">
            {`Automatically grade this contest when the game is live. Save the contest first to enable live scoring.`}
          </InfoIconDialog>
        </div>
        <div className="flex items-center justify-center py-8 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground">Save the contest first to enable live scoring</p>
        </div>
      </div>
    );
  }

  const isLoading = statusLoading || questionStatusLoading;

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Play className="w-5 h-5 text-blue-600" />
          Live Auto Scoring
        </h3>
        <InfoIconDialog title="Live Auto Scoring" ariaLabel="How live auto scoring works">
          {`How it works: When the game is live or finished and this question has a fixture, the system will automatically poll for scores and grade each period (quarter, half, final) as they complete. For finished games, use "Grade from Feed" to trigger grading. You can override automatic grading by enabling manual scoring.

Manual score during/after live: To correct or edit a score (e.g. feed wrong or late), use the Manual Score (Edit / Override) section below: copy from feed, adjust if needed, then Save Scores & Grade Contest. This is additive and does not change live scoring behaviour.`}
        </InfoIconDialog>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Current Fixture Scores from Sportradar */}
          {fixtureScoresLoading ? (
            <div className="flex items-center justify-center p-3 bg-white dark:bg-gray-800 rounded-lg">
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-muted-foreground">Loading scores...</span>
            </div>
          ) : fixtureScores?.fixture ? (
            <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg border-2 border-green-200 dark:border-green-800">
              {(() => {
                const fixture = fixtureScores.fixture;
                const awayName = fixture.awayTeam?.name || 'Away Team';
                const homeName = fixture.homeTeam?.name || 'Home Team';
                const awayAbbr = fixture.awayTeam?.abbreviation || fallbackAbbr(awayName);
                const homeAbbr = fixture.homeTeam?.abbreviation || fallbackAbbr(homeName);
                // Use styling logos (col=away, row=home) when fixture has no logoUrl
                const awayLogoUrl = fixture.awayTeam?.logoUrl || colTeamIconUrl || null;
                const homeLogoUrl = fixture.homeTeam?.logoUrl || rowTeamIconUrl || null;

                const quarterScores =
                  fixture.periodScores
                    ?.filter(p => p.periodType === 'quarter')
                    .slice()
                    .sort((a, b) => a.periodNumber - b.periodNumber) || [];

                // periodScores are period-specific points (scored in that quarter only)
                // Build CUMULATIVE totals per quarter (total score at end of that quarter)
                const cumulativeByQuarter = new Map<number, { home: number; away: number }>();
                let cumHome = 0;
                let cumAway = 0;
                for (const p of quarterScores) {
                  cumHome += p.homeScore || 0;
                  cumAway += p.awayScore || 0;
                  cumulativeByQuarter.set(p.periodNumber, { home: cumHome, away: cumAway });
                }

                const completedQuarters = new Set(quarterScores.map(p => p.periodNumber));
                const halfScores =
                  fixture.periodScores
                    ?.filter(p => p.periodType === 'half')
                    .slice()
                    .sort((a, b) => a.periodNumber - b.periodNumber) || [];
                const hasHalfPeriods = halfScores.length > 0;
                const cumulativeByHalf = new Map<number, { home: number; away: number }>();
                for (const p of halfScores) {
                  cumulativeByHalf.set(p.periodNumber, {
                    home: p.homeScore || 0,
                    away: p.awayScore || 0,
                  });
                }
                const currentQuarter = fixture.quarter ?? null;
                const sportUpper = (sport || '').toUpperCase();
                const isNCAAB = sportUpper === 'NCAAMB' || sportUpper === 'NCAAB';

                // Infer period from feed. NCAAB: quarter 1=HT, 2=FT (2-half) OR 1-2=HT, 3-4=FT (4-quarter)
                // When quarter null, infer from periodScores: 0 halves/0-1 quarters done = HT, else FT
                const completedHalfCount = halfScores.length;
                const maxCompletedQuarter =
                  quarterScores.length > 0
                    ? Math.max(...quarterScores.map(p => p.periodNumber))
                    : 0;
                const inferredFirstHalf = hasHalfPeriods
                  ? completedHalfCount === 0
                  : maxCompletedQuarter < 2;
                const inferredSecondHalf = hasHalfPeriods
                  ? completedHalfCount >= 1
                  : maxCompletedQuarter >= 2;

                const ncaabHtCurrent =
                  isNCAAB &&
                  (currentQuarter === 1 ||
                    (currentQuarter === 2 && !hasHalfPeriods && maxCompletedQuarter < 2) ||
                    (currentQuarter === null && inferredFirstHalf));
                const ncaabFtCurrent =
                  isNCAAB &&
                  ((currentQuarter === 2 && (hasHalfPeriods || maxCompletedQuarter >= 2)) ||
                    currentQuarter === 3 ||
                    currentQuarter === 4 ||
                    (currentQuarter === null && inferredSecondHalf));

                // NCAAB: 2 periods (HT, FT). Others: 4 quarters (Q1-Q4)
                type PeriodSlot = { label: string; sourceQuarter: number };
                const periodSlots: PeriodSlot[] = isNCAAB
                  ? [
                      { label: 'HT', sourceQuarter: 2 },
                      { label: 'FT', sourceQuarter: 4 },
                    ]
                  : [1, 2, 3, 4].map(q => ({ label: `Q${q}`, sourceQuarter: q }));

                const getTimeForSlot = (sourceQuarter: number, slotIndex: number) => {
                  if (fixture.status === 'finished') return '0:00';
                  if (fixture.status === 'scheduled') return '--:--';
                  if (fixture.status === 'postponed' || fixture.status === 'cancelled')
                    return '--:--';
                  if (fixture.status !== 'live') return '--:--';
                  if (isNCAAB && (currentQuarter === null || currentQuarter === 0))
                    return fixture.clock || '--:--';

                  const isInProgress = isNCAAB
                    ? (slotIndex === 0 && ncaabHtCurrent) || (slotIndex === 1 && ncaabFtCurrent)
                    : currentQuarter !== null && sourceQuarter === currentQuarter;
                  const isPast = isNCAAB
                    ? slotIndex === 0 && !ncaabHtCurrent && (ncaabFtCurrent || inferredSecondHalf)
                    : (currentQuarter ?? 0) > sourceQuarter;

                  if (isPast) return '0:00';
                  if (isInProgress) return fixture.clock || '--:--';
                  return '--:--';
                };

                const getScoresForSlot = (sourceQuarter: number, slotIndex: number) => {
                  const cumulative =
                    isNCAAB && hasHalfPeriods
                      ? cumulativeByHalf.get(slotIndex + 1) // HT=half1, FT=half2
                      : cumulativeByQuarter.get(sourceQuarter);
                  if (cumulative) {
                    return { away: cumulative.away, home: cumulative.home };
                  }
                  // Show live score only when this period is current (yellow-highlighted)
                  if (fixture.status === 'live') {
                    const isCurrentPeriod = isNCAAB
                      ? (slotIndex === 0 && ncaabHtCurrent) || (slotIndex === 1 && ncaabFtCurrent)
                      : currentQuarter !== null && sourceQuarter === currentQuarter;
                    if (isCurrentPeriod) {
                      return {
                        away: fixture.awayScore ?? '—',
                        home: fixture.homeScore ?? '—',
                      };
                    }
                  }
                  return { away: '-', home: '-' };
                };

                const isSlotComplete = (sourceQuarter: number, slotIdx: number) => {
                  if (isNCAAB && hasHalfPeriods) {
                    return slotIdx === 0
                      ? cumulativeByHalf.has(1)
                      : fixture.status === 'finished' || cumulativeByHalf.has(2);
                  }
                  if (isNCAAB) {
                    return slotIdx === 0
                      ? completedQuarters.has(2)
                      : fixture.status === 'finished' || completedQuarters.has(4);
                  }
                  return sourceQuarter === 4
                    ? fixture.status === 'finished' || completedQuarters.has(4)
                    : completedQuarters.has(sourceQuarter);
                };

                const isSlotCurrent = (sourceQuarter: number, index: number) => {
                  if (fixture.status !== 'live') return false;
                  if (isNCAAB) {
                    return index === 0 ? ncaabHtCurrent : ncaabFtCurrent;
                  }
                  return currentQuarter !== null && currentQuarter === sourceQuarter;
                };

                return (
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <TrendingUp className="w-5 h-5 text-green-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-green-800 dark:text-green-200">
                            Current Scores from Sportradar
                          </h4>
                          <Badge
                            variant={
                              fixture.status === 'finished'
                                ? 'default'
                                : fixture.status === 'live'
                                  ? 'success'
                                  : 'secondary'
                            }
                          >
                            {fixture.status.toUpperCase()}
                          </Badge>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="ml-auto gap-2"
                            onClick={() => refetchFixtureScores()}
                            disabled={fixtureScoresFetching}
                          >
                            <RefreshCw
                              className={fixtureScoresFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
                            />
                            Refresh Feed
                          </Button>
                        </div>

                        <div className="mt-3 flex items-center justify-between gap-4">
                          {/* Away */}
                          <div className="flex items-center gap-2 min-w-0">
                            {awayLogoUrl ? (
                              <img
                                src={awayLogoUrl}
                                alt={awayName}
                                className="h-9 w-9 object-contain"
                                onError={e => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="h-9 w-9 rounded bg-white/60 dark:bg-black/20 border border-green-200 dark:border-green-800" />
                            )}
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {awayName}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono">
                                {awayAbbr}
                              </div>
                            </div>
                          </div>

                          {/* Score */}
                          <div className="text-center">
                            <div className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">
                              {fixture.awayScore ?? '—'} <span className="text-gray-500">:</span>{' '}
                              {fixture.homeScore ?? '—'}
                            </div>
                          </div>

                          {/* Home */}
                          <div className="flex items-center gap-2 min-w-0 justify-end">
                            <div className="text-right min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {homeName}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono">
                                {homeAbbr}
                              </div>
                            </div>
                            {homeLogoUrl ? (
                              <img
                                src={homeLogoUrl}
                                alt={homeName}
                                className="h-9 w-9 object-contain"
                                onError={e => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="h-9 w-9 rounded bg-white/60 dark:bg-black/20 border border-green-200 dark:border-green-800" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Period cards - Q1-Q4 for NBA/NFL, HT/FT for NCAAB - hidden: not displaying properly */}
                    <div className="hidden">
                      <div
                        className={`grid gap-3 ${
                          isNCAAB
                            ? 'grid-cols-1 sm:grid-cols-2'
                            : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
                        }`}
                      >
                        {periodSlots.map((slot, i) => {
                          const time = getTimeForSlot(slot.sourceQuarter, i);
                          const scores = getScoresForSlot(slot.sourceQuarter, i);
                          const isComplete = isSlotComplete(slot.sourceQuarter, i);
                          const isCurrent = isSlotCurrent(slot.sourceQuarter, i);

                          return (
                            <div
                              key={`${slot.label}-${i}`}
                              className={`rounded-lg border p-3 ${
                                isComplete
                                  ? 'bg-green-50/70 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                                  : isCurrent
                                    ? 'bg-yellow-50/70 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
                                    : 'bg-white/70 dark:bg-gray-900/40 border-green-200 dark:border-green-800'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                                    {slot.label}
                                  </div>
                                  {isComplete && (
                                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                                  )}
                                  {isCurrent && !isComplete && (
                                    <span className="text-[10px] px-1 py-0.5 bg-yellow-200 dark:bg-yellow-800 rounded text-yellow-800 dark:text-yellow-200">
                                      LIVE
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs font-mono text-muted-foreground">
                                  {time}
                                </div>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 min-w-0">
                                    {awayLogoUrl ? (
                                      <img
                                        src={awayLogoUrl}
                                        alt={awayName}
                                        className="h-4 w-4 object-contain"
                                        onError={e => {
                                          e.currentTarget.style.display = 'none';
                                        }}
                                      />
                                    ) : null}
                                    <span className="text-xs font-mono text-gray-800 dark:text-gray-100">
                                      {awayAbbr}
                                    </span>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {scores.away}
                                  </span>
                                </div>

                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 min-w-0">
                                    {homeLogoUrl ? (
                                      <img
                                        src={homeLogoUrl}
                                        alt={homeName}
                                        className="h-4 w-4 object-contain"
                                        onError={e => {
                                          e.currentTarget.style.display = 'none';
                                        }}
                                      />
                                    ) : null}
                                    <span className="text-xs font-mono text-gray-800 dark:text-gray-100">
                                      {homeAbbr}
                                    </span>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {scores.home}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Footer status */}
                    <div className="text-xs text-center text-muted-foreground">
                      {fixture.status === 'finished'
                        ? 'GAME FINISHED - FINAL SCORE'
                        : fixture.status === 'live'
                          ? `LIVE${fixture.clock ? ` • ${fixture.clock}` : ''}`
                          : fixture.status.toUpperCase()}
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : null}

          {/* Grade from Feed Button */}
          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Auto Grade from Feed</Label>
              <InfoIconDialog
                title="Auto Grade from Feed"
                ariaLabel="Auto grade from feed information"
              >
                Manually trigger grading from Sportradar feed. Use this for finished games that
                haven&apos;t been graded yet.
              </InfoIconDialog>
            </div>
            <Button
              onClick={handleGradeFromFeed}
              disabled={disabled || manualScoreMutation.isPending || !questionId}
              variant="default"
              size="sm"
              className="gap-2"
            >
              {manualScoreMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Grading...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Grade from Feed
                </>
              )}
            </Button>
          </div>

          {/* Manual Scoring Override */}
          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2">
              <Label htmlFor="manual-scoring-toggle" className="text-sm font-medium">
                Manual Scoring Override
              </Label>
              <InfoIconDialog
                title="Manual Scoring Override"
                ariaLabel="Manual scoring override information"
              >
                When enabled, live scoring will skip this question and you can grade manually.
              </InfoIconDialog>
            </div>
            <Switch
              id="manual-scoring-toggle"
              checked={manualScoringEnabled}
              onCheckedChange={handleToggleManualScoring}
              disabled={disabled || setManualScoringMutation.isPending}
            />
          </div>
        </div>
      )}
    </div>
  );
}
